import React, { useEffect, useRef, useState } from "react";
import {
	collection, onSnapshot, addDoc, deleteDoc, doc, updateDoc, serverTimestamp,
} from "firebase/firestore";
import { supabase } from "../supabaseClient";

const MAX_IMAGE_SIZE = 10 * 1024 * 1024;

// ── Supabase storage helpers (blog covers) ────────────────────────────────────
// Separate bucket from articles/books/keynotes so blog media doesn't mix with
// other asset types. Requires a public "blog-covers" bucket in the Supabase
// dashboard (Storage → New bucket → Public bucket: ON) — same pattern as
// "article-covers", "book-covers", and "keynote-photos".

async function supabaseUploadBlogCover(file, onProgress) {
	if (!file) throw new Error("Please choose a cover image file.");
	if (file.size > MAX_IMAGE_SIZE) {
		throw new Error("Cover image is too large. The maximum size is 10 MB.");
	}
	if (!["image/jpeg", "image/png", "image/webp", "image/gif"].includes(file.type)) {
		throw new Error("Unsupported cover image type. Please choose JPEG, PNG, WebP, or GIF.");
	}
	onProgress(10);
	const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
	const path = `${Date.now()}-${cleanName}`;

	const { data, error } = await supabase.storage
		.from("blog-covers")
		.upload(path, file, {
			cacheControl: "3600",
			upsert: false,
			contentType: file.type || "image/jpeg",
		});

	if (error) throw new Error(error.message || "Cover upload failed");
	onProgress(90);

	const { data: publicUrlData } = supabase.storage
		.from("blog-covers")
		.getPublicUrl(data.path);

	onProgress(100);
	return { url: publicUrlData.publicUrl, path: data.path };
}

async function supabaseDeleteBlogCover(path) {
	if (!path) return;
	const { error } = await supabase.storage.from("blog-covers").remove([path]);
	if (error) console.error("[Supabase] blog cover delete failed:", error.message);
}

// ── Blogs Tab ──────────────────────────────────────────────────────────────────
// Manages its own "blogs" Firestore collection — kept separate from
// "articles" so the two feeds and admin lists don't interleave.
// Doc shape: { title, date, description, keywords: [], body, coverUrl,
//              coverPath, available, order }
// `body` is stored as plain text — a blank line between lines means a new
// paragraph on the public page (same convention as Articles).

const BlogsTab = ({ db }) => {
	const [blogs, setBlogs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [coverProgress, setCoverProgress] = useState(0);
	const [error, setError] = useState(null);
	const [showForm, setShowForm] = useState(false);
	const [editItem, setEditItem] = useState(null);

	const [form, setForm] = useState({
		title: "", date: "", description: "", keywordsText: "",
		body: "", available: true,
	});

	const [coverFile, setCoverFile] = useState(null);
	const [coverPreview, setCoverPreview] = useState(null);
	const coverRef = useRef();

	useEffect(() => {
		const unsub = onSnapshot(collection(db, "blogs"), (snap) => {
			const docs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
			docs.sort((a, b) => (b.order ?? 0) - (a.order ?? 0)); // newest first
			setBlogs(docs);
			setLoading(false);
		}, (err) => { setError(err.message); setLoading(false); });
		return unsub;
	}, [db]);

	const todayISO = () => {
		const d = new Date();
		return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
	};

	const resetForm = () => {
		setForm({ title: "", date: todayISO(), description: "", keywordsText: "", body: "", available: true });
		setCoverFile(null); setCoverPreview(null);
		setEditItem(null); setShowForm(false);
	};

	const openNew = () => {
		resetForm();
		setForm(f => ({ ...f, date: todayISO() }));
		setShowForm(true);
	};

	const openEdit = (item) => {
		setEditItem(item);
		setForm({
			title: item.title || "",
			date: item.date || todayISO(),
			description: item.description || "",
			keywordsText: (item.keywords || []).join(", "),
			body: item.body || "",
			available: item.available !== false,
		});
		setCoverPreview(item.coverUrl || null);
		setCoverFile(null);
		setShowForm(true);
	};

	const handleCoverChange = (e) => {
		const file = e.target.files[0];
		if (!file) return;
		setCoverFile(file);
		setCoverPreview(URL.createObjectURL(file));
	};

	const handleSubmit = async () => {
		if (!form.title.trim() || !form.body.trim()) {
			setError("Title and body are required.");
			return;
		}
		setError(null);
		setSaving(true);
		setCoverProgress(0);

		try {
			let coverUrl = editItem?.coverUrl || "";
			let coverPath = editItem?.coverPath || "";

			if (coverFile) {
				const result = await supabaseUploadBlogCover(coverFile, setCoverProgress);
				coverUrl = result.url;
				coverPath = result.path;
				if (editItem?.coverPath) await supabaseDeleteBlogCover(editItem.coverPath);
			}

			const keywords = form.keywordsText
				.split(",")
				.map(k => k.trim())
				.filter(Boolean);

			const payload = {
				title: form.title.trim(),
				date: form.date || todayISO(),
				description: form.description.trim(),
				keywords,
				body: form.body,
				coverUrl,
				coverPath,
				available: form.available,
				updatedAt: serverTimestamp(),
			};

			if (editItem) {
				await updateDoc(doc(db, "blogs", editItem.id), payload);
			} else {
				await addDoc(collection(db, "blogs"), {
					...payload,
					order: Date.now(),
					createdAt: serverTimestamp(),
				});
			}
			resetForm();
		} catch (err) {
			setError(err.message);
		} finally {
			setSaving(false);
			setCoverProgress(0);
		}
	};

	const handleDelete = async (item) => {
		if (!window.confirm(`Delete "${item.title}"? This cannot be undone.`)) return;
		try {
			await deleteDoc(doc(db, "blogs", item.id));
			if (item.coverPath) await supabaseDeleteBlogCover(item.coverPath);
		} catch (err) { setError(err.message); }
	};

	return (
		<div className="adm-books-tab">
			{error && <div className="adm-query-error">{error}</div>}

			<div className="adm-books-header">
				<div>
					<div className="adm-books-title">Blog</div>
					<div className="adm-books-sub">{blogs.length} post{blogs.length !== 1 ? "s" : ""} published</div>
				</div>
				<button className="adm-primary-btn" onClick={openNew}>
					+ Add blog post
				</button>
			</div>

			{showForm && (
				<div className="adm-book-form">
					<div className="adm-book-form__title">{editItem ? "Edit blog post" : "Add new blog post"}</div>

					<div className="adm-book-form__grid">
						{/* Cover upload */}
						<div
							className="adm-cover-upload"
							onClick={() => coverRef.current?.click()}
							style={coverPreview ? { backgroundImage: `url(${coverPreview})` } : {}}
						>
							{!coverPreview && (
								<div className="adm-cover-upload__placeholder">
									<span className="adm-cover-upload__icon">📝</span>
									<span>Upload cover (optional)</span>
								</div>
							)}
							<input
								ref={coverRef}
								type="file"
								accept="image/*"
								style={{ display: "none" }}
								onChange={handleCoverChange}
							/>
						</div>

						{/* Fields */}
						<div className="adm-book-form__fields">
							<div className="adm-field-row adm-field-row--half">
								<div>
									<label className="adm-field-label">Title *</label>
									<input
										className="adm-field-input"
										value={form.title}
										onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
										placeholder="Blog post title"
									/>
								</div>
								<div>
									<label className="adm-field-label">Date</label>
									<input
										className="adm-field-input"
										type="date"
										value={form.date}
										onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
									/>
								</div>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Description (for SEO / preview)</label>
								<textarea
									className="adm-field-input adm-field-textarea"
									value={form.description}
									onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
									placeholder="One or two sentences summarising the post"
									rows={2}
								/>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Keywords (comma-separated)</label>
								<input
									className="adm-field-input"
									value={form.keywordsText}
									onChange={e => setForm(f => ({ ...f, keywordsText: e.target.value }))}
									placeholder="e.g. wellbeing, mindset, relationships"
								/>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Body *</label>
								<textarea
									className="adm-field-input adm-field-textarea"
									value={form.body}
									onChange={e => setForm(f => ({ ...f, body: e.target.value }))}
									placeholder="Write the post here. Leave a blank line between paragraphs."
									rows={10}
								/>
							</div>

							<div className="adm-field-row">
								<label className="adm-field-label">Available?</label>
								<div className="adm-toggle-row">
									<label className="adm-toggle">
										<input
											type="checkbox"
											checked={form.available}
											onChange={e => setForm(f => ({ ...f, available: e.target.checked }))}
										/>
										<span className="adm-toggle__track"><span className="adm-toggle__thumb" /></span>
									</label>
									<span className="adm-toggle-label">{form.available ? "Published" : "Hidden"}</span>
								</div>
							</div>
						</div>
					</div>

					{saving && coverFile && (
						<div className="adm-upload-progress">
							<div className="adm-upload-progress__bar" style={{ width: `${coverProgress}%` }} />
							<span>Cover {coverProgress}%</span>
						</div>
					)}

					<div className="adm-book-form__actions">
						<button className="adm-ghost-btn" onClick={resetForm} disabled={saving}>Cancel</button>
						<button className="adm-primary-btn" onClick={handleSubmit} disabled={saving}>
							{saving ? "Saving…" : editItem ? "Save changes" : "Publish post"}
						</button>
					</div>
				</div>
			)}

			{loading ? (
				<div className="adm-bookings-empty">Loading blog posts…</div>
			) : blogs.length === 0 ? (
				<div className="adm-bookings-empty">
					<div className="adm-bookings-empty__icon">📝</div>
					<div>No blog posts yet. Add your first one above.</div>
				</div>
			) : (
				<div className="adm-book-list">
					{blogs.map(item => (
						<div key={item.id} className={`adm-book-card${!item.available ? " adm-book-card--unavailable" : ""}`}>
							{item.coverUrl
								? <img className="adm-book-card__cover" src={item.coverUrl} alt={item.title} />
								: <div className="adm-book-card__cover adm-book-card__cover--placeholder">📝</div>
							}
							<div className="adm-book-card__body">
								<div className="adm-book-card__title">{item.title}</div>
								<div className="adm-book-card__author">{item.date}</div>
								{item.description && <div className="adm-book-card__desc">{item.description}</div>}
								<div className="adm-book-card__footer">
									{!item.available && <span className="adm-badge adm-badge--muted">Hidden</span>}
								</div>
							</div>
							<div className="adm-book-card__actions">
								<button className="adm-icon-btn" title="Edit" onClick={() => openEdit(item)}>✏️</button>
								<button className="adm-icon-btn adm-icon-btn--danger" title="Delete" onClick={() => handleDelete(item)}>🗑</button>
							</div>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

export default BlogsTab;